/**
 * Suppresses HTML implicit form submission (pressing Enter inside a field) for
 * forms that opt in with `data-js-suppress-implicit-submit`.
 *
 * IP-15054: the proposal fee step renders the whole spreadsheet as one form, so
 * Enter in any material/service cell fired the form's default button and posted
 * the step, wiping the edited row. Enter is a reflex keystroke in a grid UI, and
 * a per-page keydown handler would miss the rows nested_form_fields adds after
 * load, so the listener is delegated from the document and matches on the
 * event target's form owner.
 */
export const SUPPRESS_IMPLICIT_SUBMIT_ATTRIBUTE = 'data-js-suppress-implicit-submit';
const ENTER_KEY = 'Enter';
/** Input types whose default Enter action is to activate the control, not to submit the form. */
const ACTIVATABLE_INPUT_TYPES = ['submit', 'reset', 'button', 'image'];
/** Elements where Enter has its own meaning that must survive. */
const EXEMPT_TAG_NAMES = ['TEXTAREA', 'BUTTON'];
/**
 * Resolves the form that owns an event target, or null when the target is not a
 * form-associated element.
 */
function ownerForm(target) {
    var _a;
    if (!(target instanceof Element))
        return null;
    // Reading `.form` (rather than `.closest('form')`) honours the HTML `form=`
    // attribute, so a field physically nested in an opted-in form but owned by a
    // different one — e.g. the fee step's deficiency search box — keeps its Enter.
    return (_a = target.form) !== null && _a !== void 0 ? _a : null;
}
/**
 * True when Enter on this target would implicitly submit an opted-in form and
 * should therefore be cancelled. False for textareas, buttons, activatable
 * inputs, and anything owned by a form that has not opted in.
 */
export function shouldSuppressImplicitSubmit(target) {
    const form = ownerForm(target);
    if (!form || !form.hasAttribute(SUPPRESS_IMPLICIT_SUBMIT_ATTRIBUTE))
        return false;
    const element = target;
    if (EXEMPT_TAG_NAMES.includes(element.tagName))
        return false;
    if (element instanceof HTMLInputElement && ACTIVATABLE_INPUT_TYPES.includes(element.type)) {
        return false;
    }
    return true;
}
/** Keydown listener that cancels Enter-triggered implicit submission. */
export function handleImplicitSubmitKeydown(event) {
    if (event.key !== ENTER_KEY || event.isComposing || event.defaultPrevented)
        return;
    if (!shouldSuppressImplicitSubmit(event.target))
        return;
    // Only the browser's default action is cancelled. Propagation is left alone so
    // component-level Enter handlers (react-select, autocompletes) still run.
    event.preventDefault();
}
/** Installs the delegated listener. Returns a teardown for tests and hot reloads. */
export function installImplicitSubmitSuppressor(root = document) {
    root.addEventListener('keydown', handleImplicitSubmitKeydown);
    return () => root.removeEventListener('keydown', handleImplicitSubmitKeydown);
}
